import { BaseApiService } from '@doccareplus/api-client';

/**
 * Patients infrastructure — HTTP adapter for the clinic patient directory
 * (search/list, register, chart detail, archive/restore).
 *
 * Model B header-based auth: the clinic id and active role are injected globally
 * by AuthSessionBridge, so no clinic id lives in any URL or request body — only
 * the patient id the client actually knows.
 *
 * All JSON is camelCase; these types mirror the C# Contracts records 1:1.
 */

/* ─── Pagination ───────────────────────────────────────────── */

/** Mirrors Contracts.Common.PaginatedResponse<T>. */
export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/* ─── Responses ────────────────────────────────────────────── */

/** Mirrors Patients.Common.PatientListItemResponse (lean list row). */
export interface PatientListItemResponse {
  id: string;
  patientNumber: string;
  fullName: string;
  /** ISO date (yyyy-MM-dd) or null. */
  dateOfBirth?: string | null;
  age?: number | null;
  gender: string;
  phone?: string | null;
  /** "Unknown" | "APos" | "ANeg" | … */
  bloodGroup: string;
  allergySummary?: string | null;
  /** "Active" | "Archived" */
  status: string;
  /** ISO datetime the patient was registered. */
  createdAt: string;
}

/** Mirrors Tags.Common.TagDefinitionResponse (clinic tag catalog row). */
export interface ClinicTagResponse {
  id: string;
  name: string;
  color?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors Patients.Common.PatientResponse (full demographic record). */
export interface PatientResponse {
  id: string;
  patientNumber: string;
  fullName: string;
  /** ISO date (yyyy-MM-dd) or null. */
  dateOfBirth?: string | null;
  age?: number | null;
  /** "Male" | "Female" | "Other" | "Unspecified" */
  gender: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  /** "Unknown" | "APos" | "ANeg" | … */
  bloodGroup: string;
  allergySummary?: string | null;
  notes?: string | null;
  /** "Active" | "Archived" */
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors Patients.Common.PatientAlertResponse. */
export interface PatientAlertResponse {
  id: string;
  /** "Allergy" | "Critical" | "Warning" | "Note" */
  type: string;
  /** "Active" | "Resolved" */
  status: string;
  /** Derived from type: Critical, High, Moderate, or Low. */
  severity: string;
  description: string;
  category?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors Patients.Common.PatientTagResponse. */
export interface PatientTagResponse {
  tagDefinitionId: string;
  name: string;
  color?: string | null;
  isActive: boolean;
}

/** Mirrors Patients.Common.PatientHistoryEntryResponse. */
export interface PatientHistoryEntryResponse {
  id: string;
  /** "Condition" | "Medication" | "Surgery" | "Immunization" */
  category: string;
  /** "Active" | "Resolved" */
  status: string;
  name: string;
  detail?: string | null;
  occurred?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors Patients.Common.PatientNoteEntryResponse. */
export interface PatientNoteEntryResponse {
  id: string;
  /** "FamilyHistory" | "LifestyleSocial" */
  category: string;
  label: string;
  value: string;
}

/** Mirrors Patients.Common.PatientLastVitalsResponse. */
export interface PatientLastVitalsResponse {
  systolic?: number | null;
  diastolic?: number | null;
  pulse?: number | null;
  temperature?: number | null;
  temperatureUnit?: string | null;
}

/** Mirrors Patients.Common.PatientChartSummaryResponse. */
export interface PatientChartSummaryResponse {
  visitCount: number;
  lastVisitDate?: string | null;
  lastVisitVitals?: PatientLastVitalsResponse | null;
  lastVisitDiagnosis?: string | null;
  lastVisitChiefComplaint?: string | null;
  lastVisitProviderName?: string | null;
  nextAppointmentDate?: string | null;
  nextAppointmentProviderName?: string | null;
}

/** Mirrors Patients.Common.PatientDetailResponse (chart payload). */
export interface PatientDetailResponse {
  patient: PatientResponse;
  alerts: PatientAlertResponse[];
  tags: PatientTagResponse[];
  historyEntries: PatientHistoryEntryResponse[];
  notes: PatientNoteEntryResponse[];
  chartSummary: PatientChartSummaryResponse;
}

/** A patient-submitted demographic correction awaiting clinic review. */
export interface PatientDetailCorrectionResponse {
  id: string;
  note: string;
  /** "Pending" | "Reviewed" */
  status: string;
  requestedAt: string;
  reviewedAt?: string | null;
  reviewedByUserId?: string | null;
}

/* ─── Requests ─────────────────────────────────────────────── */

/** Allow-listed status filter values (maps to the server `Status` query param). */
export type PatientStatusFilter = 'Active' | 'Archived' | 'All';

/** Allow-listed sort columns (maps to the server `SortField` query param). */
export type PatientSortField = 'name' | 'patientNumber' | 'age' | 'registeredAt';

export type SortDirection = 'asc' | 'desc';

/** Query for GET /clinic/patients (SearchPatientsRequest). Omit a field to leave that filter off. */
export interface SearchPatientsParams {
  search?: string;
  /** "Active" (default) | "Archived" | "All". */
  status?: PatientStatusFilter;
  /** "Male" | "Female" | "Other" | "Unspecified". */
  gender?: string;
  /** "APos" | "ANeg" | … | "Unknown". */
  bloodGroup?: string;
  minAge?: number;
  maxAge?: number;
  hasAllergies?: boolean;
  hasActiveAlerts?: boolean;
  /** Clinic tag ids; a patient matches if assigned ANY of them. */
  tagIds?: string[];
  sortField?: PatientSortField;
  sortDirection?: SortDirection;
  page: number;
  pageSize: number;
}

export interface PatientDuplicateMatchResponse {
  id: string; patientNumber: string; fullName: string; dateOfBirth?: string | null;
  phone?: string | null; email?: string | null; status: string; matchReasons: string[];
}

export interface CheckPatientDuplicatesRequest {
  fullName: string; phone: string; email?: string | null; dateOfBirth?: string | null;
}

export interface SaveClinicTagRequest {
  name: string;
  color?: string | null;
}

/** Body for POST /clinic/patients (CreatePatientRequest). Name and E.164 phone are required. */
export interface CreatePatientRequest {
  fullName: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  bloodGroup?: string | null;
  allergySummary?: string | null;
  notes?: string | null;
  duplicateOverrideReason?: string | null;
}

export interface UpdatePatientRequest {
  fullName: string;
  dateOfBirth?: string | null;
  gender?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  bloodGroup?: string | null;
  notes?: string | null;
  expectedUpdatedAt: string;
}

export interface SavePatientAlertRequest { type: string; description: string; category?: string | null }
export interface AddHistoryEntryRequest { category: string; name: string; detail?: string | null; occurred?: string | null }
export interface UpdateHistoryEntryRequest { name: string; detail?: string | null; occurred?: string | null }
export interface NoteEntryInput { label: string; value: string }
export interface MergePatientRequest { targetPatientId: string; reason: string }
export interface MergePatientResponse {
  retainedPatientId: string; archivedPatientId: string; movedAppointments: number; movedVisits: number;
  movedInvoices: number; movedAlerts: number; movedTags: number; movedPortalLinks: number;
}

class PatientsApiService extends BaseApiService {
  constructor() {
    super('/clinic');
  }

  search(params: SearchPatientsParams): Promise<PaginatedResponse<PatientListItemResponse>> {
    // `indexes: null` repeats array params as `tagIds=a&tagIds=b`, which ASP.NET model binding expects for
    // `List<Guid>` (the default `tagIds[]=…` form does not bind).
    return this.get<PaginatedResponse<PatientListItemResponse>>('/patients', {
      params,
      paramsSerializer: { indexes: null },
    });
  }

  /** The clinic's active tag catalog (for the directory tag filter). Fetches a single generous page. */
  async listTags(): Promise<ClinicTagResponse[]> {
    const response = await this.get<PaginatedResponse<ClinicTagResponse>>('/tags', {
      params: { includeInactive: false, page: 1, pageSize: 100 },
    });
    return response.items;
  }

  createTag(body: SaveClinicTagRequest): Promise<ClinicTagResponse> {
    return this.post<ClinicTagResponse, SaveClinicTagRequest>('/tags', body);
  }

  updateTag(tagId: string, body: SaveClinicTagRequest): Promise<ClinicTagResponse> {
    return this.put<ClinicTagResponse, SaveClinicTagRequest>(`/tags/${tagId}`, body);
  }

  getById(patientId: string): Promise<PatientDetailResponse> {
    return this.get<PatientDetailResponse>(`/patients/${patientId}`);
  }

  listDetailCorrections(patientId: string): Promise<PatientDetailCorrectionResponse[]> {
    return this.get<PatientDetailCorrectionResponse[]>(`/patients/${patientId}/detail-corrections`);
  }

  reviewDetailCorrection(patientId: string, correctionRequestId: string): Promise<PatientDetailCorrectionResponse> {
    return this.post<PatientDetailCorrectionResponse>(
      `/patients/${patientId}/detail-corrections/${correctionRequestId}/review`
    );
  }

  register(body: CreatePatientRequest): Promise<PatientResponse> {
    return this.post<PatientResponse, CreatePatientRequest>('/patients', body);
  }

  update(patientId: string, body: UpdatePatientRequest): Promise<PatientResponse> {
    return this.put<PatientResponse, UpdatePatientRequest>(`/patients/${patientId}`, body);
  }

  setAllergySummary(patientId: string, allergySummary?: string | null): Promise<PatientResponse> {
    return this.put<PatientResponse, { allergySummary?: string | null }>(`/patients/${patientId}/allergy-summary`, { allergySummary });
  }

  addAlert(patientId: string, body: SavePatientAlertRequest): Promise<PatientAlertResponse> {
    return this.post<PatientAlertResponse, SavePatientAlertRequest>(`/patients/${patientId}/alerts`, body);
  }

  updateAlert(patientId: string, alertId: string, body: SavePatientAlertRequest): Promise<PatientAlertResponse> {
    return this.put<PatientAlertResponse, SavePatientAlertRequest>(`/patients/${patientId}/alerts/${alertId}`, body);
  }

  resolveAlert(patientId: string, alertId: string): Promise<PatientAlertResponse> {
    return this.post<PatientAlertResponse>(`/patients/${patientId}/alerts/${alertId}/resolve`);
  }

  reopenAlert(patientId: string, alertId: string): Promise<PatientAlertResponse> {
    return this.post<PatientAlertResponse>(`/patients/${patientId}/alerts/${alertId}/reopen`);
  }

  addHistoryEntry(patientId: string, body: AddHistoryEntryRequest): Promise<PatientHistoryEntryResponse> {
    return this.post<PatientHistoryEntryResponse, AddHistoryEntryRequest>(`/patients/${patientId}/history`, body);
  }

  updateHistoryEntry(patientId: string, entryId: string, body: UpdateHistoryEntryRequest): Promise<PatientHistoryEntryResponse> {
    return this.put<PatientHistoryEntryResponse, UpdateHistoryEntryRequest>(`/patients/${patientId}/history/${entryId}`, body);
  }

  resolveHistoryEntry(patientId: string, entryId: string): Promise<PatientHistoryEntryResponse> {
    return this.post<PatientHistoryEntryResponse>(`/patients/${patientId}/history/${entryId}/resolve`);
  }

  reopenHistoryEntry(patientId: string, entryId: string): Promise<PatientHistoryEntryResponse> {
    return this.post<PatientHistoryEntryResponse>(`/patients/${patientId}/history/${entryId}/reopen`);
  }

  replaceFamilyHistory(patientId: string, entries: NoteEntryInput[]): Promise<PatientNoteEntryResponse[]> {
    return this.put<PatientNoteEntryResponse[], { entries: NoteEntryInput[] }>(`/patients/${patientId}/family-history`, { entries });
  }

  replaceLifestyleSocial(patientId: string, entries: NoteEntryInput[]): Promise<PatientNoteEntryResponse[]> {
    return this.put<PatientNoteEntryResponse[], { entries: NoteEntryInput[] }>(`/patients/${patientId}/lifestyle`, { entries });
  }

  assignTag(patientId: string, tagDefinitionId: string): Promise<PatientTagResponse[]> {
    return this.post<PatientTagResponse[], { tagDefinitionId: string }>(`/patients/${patientId}/tags`, { tagDefinitionId });
  }

  removeTag(patientId: string, tagDefinitionId: string): Promise<PatientTagResponse[]> {
    return this.delete<PatientTagResponse[]>(`/patients/${patientId}/tags/${tagDefinitionId}`);
  }

  merge(sourcePatientId: string, body: MergePatientRequest): Promise<MergePatientResponse> {
    return this.post<MergePatientResponse, MergePatientRequest>(`/patients/${sourcePatientId}/merge`, body);
  }

  checkDuplicates(body: CheckPatientDuplicatesRequest): Promise<PatientDuplicateMatchResponse[]> {
    return this.post<PatientDuplicateMatchResponse[], CheckPatientDuplicatesRequest>('/patients/duplicates/check', body);
  }

  archive(patientId: string): Promise<PatientResponse> {
    return this.post<PatientResponse>(`/patients/${patientId}/archive`);
  }

  restore(patientId: string): Promise<PatientResponse> {
    return this.post<PatientResponse>(`/patients/${patientId}/restore`);
  }
}

export const patientsApi = new PatientsApiService();
